import { ImportJobRepresentation } from '@common/api';
import { useUpdateImportJob } from '@data/importJob/importJobActions';

export const useLabelActions = (selectedJob: ImportJobRepresentation | null, importJobs: ImportJobRepresentation[]) => {
    const updateImportJob = useUpdateImportJob();

    const updateLabelsForJob = async (jobId: number, updatedLabels: string[]) => {
        await updateImportJob.mutateAsync({
            id: jobId,
            labels: updatedLabels,
        });
    };

    const addLabel = async (newLabel: string) => {
        if (!selectedJob) return;
        const updatedLabels = [...new Set([...selectedJob.labels, newLabel])];
        await updateLabelsForJob(selectedJob.id, updatedLabels);
    };

    const removeLabel = async (label: string) => {
        if (!selectedJob) return;
        const updatedLabels = selectedJob.labels.filter((l) => l !== label);
        await updateLabelsForJob(selectedJob.id, updatedLabels);
    };

    const renameLabel = async (oldLabel: string, newLabel: string) => {
        if (!selectedJob) return;
        const updatedSelectedJobLabels = selectedJob.labels.map((label) => (label === oldLabel ? newLabel : label));
        await updateLabelsForJob(selectedJob.id, updatedSelectedJobLabels);

        const jobsToUpdate = importJobs.filter((job) => job.labels.includes(oldLabel));
        await Promise.all(
            jobsToUpdate.map((job) =>
                updateImportJob.mutateAsync({
                    id: job.id,
                    labels: job.labels.map((label) => (label === oldLabel ? newLabel : label)),
                })
            )
        );
    };

    const createLabel = async (newLabel: string) => {
        if (!selectedJob) return;
        const updatedLabels = [...new Set([...selectedJob.labels, newLabel])];
        try {
            await updateLabelsForJob(selectedJob.id, updatedLabels);
            return newLabel;
        } catch {
            return undefined;
        }
    };

    const deleteLabel = async (labelToDelete: string) => {
        if (!selectedJob) return;

        const updatedSelectedJobLabels = selectedJob.labels.filter((l) => l !== labelToDelete);
        await updateLabelsForJob(selectedJob.id, updatedSelectedJobLabels);

        const jobsToUpdate = importJobs.filter((job) => job.labels?.includes(labelToDelete));
        await Promise.all(
            jobsToUpdate.map((job) =>
                updateImportJob.mutateAsync({
                    id: job.id,
                    labels: job.labels?.filter((l) => l !== labelToDelete) || [],
                })
            )
        );
    };

    return {
        addLabel,
        removeLabel,
        renameLabel,
        createLabel,
        deleteLabel,
    };
};
